import { useBulkUpdateStudentsMutation } from '@/app/redux/endpoints/students';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { generateStudentPassword } from '@/common/utils/student';
import api from '@/db/apiClient';
import {
  CircleNotchIcon,
  CopySimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  ShuffleIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Input } from '@shadcn/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { chunk } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface SignInEntry {
  username: string;
  password: string;
  passwordVisible: boolean;
}

interface BulkUpdateSignInsProps {
  open: boolean;
  onClose: () => void;
  students: Student[];
}

function initEntries(students: Student[]): Record<string, SignInEntry> {
  return Object.fromEntries(
    students.map((s) => [
      s.id,
      {
        username: s.spellcastUsername,
        password: generateStudentPassword(),
        passwordVisible: false,
      },
    ]),
  );
}

export default function BulkUpdateSignIns({
  open,
  onClose,
  students,
}: BulkUpdateSignInsProps) {
  const [bulkUpdateStudents] = useBulkUpdateStudentsMutation();
  const { currentOrganisationId } = useCurrentOrganisationId();

  const [entries, setEntries] = useState<Record<string, SignInEntry>>(() =>
    initEntries(students),
  );
  const [generatingUsernames, setGeneratingUsernames] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEntries((prev) => {
      const next = { ...prev };
      const currentIds = new Set(students.map((s) => s.id));
      // Add entries for newly selected students
      for (const s of students) {
        if (!next[s.id]) {
          next[s.id] = {
            username: s.spellcastUsername,
            password: generateStudentPassword(),
            passwordVisible: false,
          };
        }
      }
      // Remove entries for deselected students
      for (const id of Object.keys(next)) {
        if (!currentIds.has(id)) {
          delete next[id];
        }
      }
      return next;
    });
  }, [open, students]);

  const allPasswordsVisible = useMemo(() => {
    return Object.values(entries).every((e) => e.passwordVisible);
  }, [entries]);

  const toggleAllPasswordsVisible = useCallback(
    () =>
      setEntries((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([id, e]) => [
            id,
            { ...e, passwordVisible: !allPasswordsVisible },
          ]),
        ),
      ),
    [allPasswordsVisible],
  );

  function updateEntry(studentId: string, fields: Partial<SignInEntry>) {
    setEntries((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], ...fields },
    }));
  }

  function shuffleAll() {
    setEntries((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, e]) => [
          id,
          { ...e, password: generateStudentPassword() },
        ]),
      ),
    );
  }

  function randomiseAllSamePassword() {
    const pw = generateStudentPassword();
    setEntries((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, e]) => [id, { ...e, password: pw }]),
      ),
    );
  }

  function copyPasswordToAll(studentId: string) {
    const pw = entries[studentId].password;
    setEntries((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, e]) => [id, { ...e, password: pw }]),
      ),
    );
  }

  async function randomiseAllUsernames() {
    setGeneratingUsernames(true);
    try {
      const CHUNK = 50;
      const allUsernames: string[] = [];
      for (let i = 0; i < students.length; i += CHUNK) {
        const batch = students.slice(i, i + CHUNK);
        const { usernames } = await api.asEducator.generateStudentUsernames({
          names: batch.map((s) => ({
            firstName: s.studentFirstName.trim(),
            lastName: s.studentSurname.trim(),
          })),
        });
        allUsernames.push(...usernames);
      }
      setEntries((prev) => {
        const next = { ...prev };
        students.forEach((s, i) => {
          if (next[s.id]) {
            next[s.id] = {
              ...next[s.id],
              username: allUsernames[i] ?? next[s.id].username,
            };
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to generate usernames', err);
      toast.error('Failed to generate usernames');
    } finally {
      setGeneratingUsernames(false);
    }
  }

  async function handleSave() {
    if (!currentOrganisationId) {
      console.error('Missing org ID');
      toast.error('Failed to update sign-in details', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const allUpdates = students.map((s) => ({
      studentId: s.id,
      spellcastUsername: entries[s.id].username,
      password: entries[s.id].password,
    }));
    const chunks = chunk(allUpdates, 50);
    let updated = 0;

    onClose();
    const toastId = toast.loading('Updating learner sign-ins...');

    try {
      for (const c of chunks) {
        await bulkUpdateStudents({
          organisationId: currentOrganisationId,
          updates: c,
        }).unwrap();
        updated += c.length;
        toast.loading(
          `Updating learner sign-ins (${updated}/${allUpdates.length})`,
          { id: toastId },
        );
      }
      toast.success('Learner sign-ins updated', {
        id: toastId,
        description: '',
      });
    } catch (error) {
      console.error('Error updating learner sign-ins:', error);
      if (updated > 0) {
        toast.error('Failed to update learner sign-ins', {
          id: toastId,
          description: `Updated ${updated} of ${allUpdates.length}. Please retry.`,
        });
      } else {
        toast.error('Failed to update learner sign-ins', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    }
  }

  if (!students.length) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Change Sign-In Details</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-2 my-2">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 px-1 text-sm font-medium text-muted-foreground items-center">
            <span>Learner</span>
            <div className="flex items-center gap-1">
              <span>Username</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={randomiseAllUsernames}
                    disabled={generatingUsernames}
                  >
                    {generatingUsernames ? (
                      <CircleNotchIcon className="size-4 animate-spin" />
                    ) : (
                      <ShuffleIcon className="size-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Randomise all usernames</TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-1">
              <span>New Password</span>
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="size-6">
                        <ShuffleIcon className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Randomise all passwords</TooltipContent>
                </Tooltip>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={shuffleAll}>
                    Unique for each
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={randomiseAllSamePassword}>
                    Same for all
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={toggleAllPasswordsVisible}
                  >
                    {allPasswordsVisible ? (
                      <EyeSlashIcon className="size-4" />
                    ) : (
                      <EyeIcon className="size-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {allPasswordsVisible ? 'Hide' : 'Show'} all passwords
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="w-8" />
          </div>

          {/* Scrollable learner rows */}
          <div className="max-h-[40vh] overflow-y-auto space-y-2 py-1 pr-1">
            {students.map((student) => {
              const entry = entries[student.id];
              if (!entry) return null;
              return (
                <div
                  key={student.id}
                  className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center"
                >
                  <span className="text-sm truncate pl-1">
                    {student.studentFirstName} {student.studentSurname}
                  </span>

                  <Input
                    className="h-9"
                    value={entry.username}
                    onChange={(e) =>
                      updateEntry(student.id, { username: e.target.value })
                    }
                  />

                  <div className="relative">
                    <Input
                      className="h-9 pr-9"
                      type={entry.passwordVisible ? 'text' : 'password'}
                      value={entry.password}
                      onChange={(e) =>
                        updateEntry(student.id, { password: e.target.value })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 absolute right-1 top-1/2 -translate-y-1/2"
                      onClick={() =>
                        updateEntry(student.id, {
                          passwordVisible: !entry.passwordVisible,
                        })
                      }
                    >
                      {entry.passwordVisible ? (
                        <EyeSlashIcon className="size-4" />
                      ) : (
                        <EyeIcon className="size-4" />
                      )}
                    </Button>
                  </div>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyPasswordToAll(student.id)}
                      >
                        <CopySimpleIcon className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy password to all</TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
